import React, { useState, useEffect } from "react";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styles from './CreateAppointment.module.css';

const CreateAppointment = ({ onClose, fetchAppointments }) => {
    const [customReason, setCustomReason] = useState('');
    const [patientName, setPatientName] = useState('');
    const [email, setEmail] = useState('');
    const [reason, setReason] = useState('');
    const [date, setDate] = useState(new Date());
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [dentist, setDentist] = useState('');
    const [dentistList, setDentistList] = useState([]);
    const [error, setError] = useState(null);
    const [emptyFields, setEmptyFields] = useState([]);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/currentExternalUser', {
                    credentials: 'include',
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch user');
                }
                const user = await response.json();
                setPatientName(`${user.firstName} ${user.lastName}`);
                setEmail(user.email);
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };

        const fetchDentists = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/dentists');
                if (!response.ok) {
                    throw new Error('Failed to fetch dentists');
                }
                const dentists = await response.json();
                setDentistList(dentists.map(dentist => dentist.lastName));
            } catch (error) {
                console.error('Error fetching dentists:', error);
            }
        };

        fetchUser();
        fetchDentists();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const appointment = {
            patientName,
            email,
            reason: reason === "custom" ? customReason : reason,
            date: date.toLocaleDateString('en-CA'),
            startTime,
            endTime,
            dentist
        };

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/appointments', {
                method: 'POST',
                body: JSON.stringify(appointment),
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (!response.ok) {
                const json = await response.json();
                setError(json.error);
                setEmptyFields(json.emptyFields || []);
                return;
            }

            const newAppointment = await response.json();
            fetchAppointments(); // Call the parent function to fetch updated appointments
            onClose(); // Close the modal after successful submission
        } catch (error) {
            console.error('Error creating appointment:', error);
            setError('Failed to create appointment. Please try again.');
        }
    };

    const handleDateChange = (newDate) => setDate(newDate);

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.flex}>
                    <button className={styles.closeModal} onClick={onClose}>×</button>
                    <h3 className={styles.h3}>Create Appointment</h3>
                </div>
                <div className={styles.modalDivider} />
                <form onSubmit={handleSubmit} className={styles.formContainer}>
                    <div className={styles.horizontalGroup}>
                        <div className={styles.formGroup}>
                            <label>Patient Name*</label>
                            <input type="text" value={patientName} readOnly />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Email*</label>
                            <input type="email" value={email} readOnly />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Reason*</label>
                            <input
                                type="text"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Type your reason"
                                required
                                className={emptyFields.includes('reason') ? styles.error : ''}
                            />
                        </div>
                    </div>

                    <div className={styles.calendarContainer}>
                        <div className={styles.calendarWrapper}>
                            <label>Appointment Date*</label>
                            <Calendar
                                onChange={handleDateChange}
                                value={date}
                                prev2Label={null}
                                next2Label={null}
                            />
                        </div>

                        <div className={styles.rightSideWrapper}>
                            <div className={styles.timeInput}>
                                <label>Start Time*</label>
                                <input
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                    required
                                    className={emptyFields.includes('startTime') ? styles.error : ''}
                                />
                            </div>
                            <div className={styles.timeInput}>
                                <label>End Time*</label>
                                <input
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                    required
                                    className={emptyFields.includes('endTime') ? styles.error : ''}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Dentist*</label>
                                <select
                                    value={dentist}
                                    onChange={(e) => setDentist(e.target.value)}
                                    className={emptyFields.includes('dentist') ? styles.error : ''}
                                >
                                    <option value="">Select Dentist</option>
                                    {dentistList.map((lastName, index) => (
                                        <option key={index} value={lastName}>{lastName}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </form>
                <div className={styles.modalDividerBottom} />
                <button type="submit">Create Appointment</button>
            </div>
        </div>
    );
};

export default CreateAppointment;
